import React, { useEffect, useState } from 'react';
import { HeartIcon, ChatBubbleLeftIcon, ArrowPathRoundedSquareIcon } from '@heroicons/react/24/solid';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ThemeBadge from '../components/ThemeBadge';
import { formatDateTime } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || '';

function Post({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPost = async () => {
    const { data } = await axios.get(`${API_URL}/api/posts/${id}`, { headers });
    setPost(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/posts/${id}/comments`, { content: newComment }, { headers });
      setNewComment('');
      fetchPost();
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p className="text-sm text-slate-400">Carregando…</p>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p className="text-sm text-slate-400">Post não encontrado.</p>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <button onClick={() => navigate(-1)} className="mb-4 text-sm text-slate-500 hover:text-slate-700">← Voltar</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">
              {post.influencer?.display_name || 'Perfil sem identificação'}
              {post.influencer?.platform && <span className="ml-2 text-xs font-normal text-slate-400">· {post.influencer.platform}</span>}
            </p>
            <p className="text-xs text-slate-400">{formatDateTime(post.published_at)}</p>
          </div>
          {post.relevance_score != null && (
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
              {post.relevance_score}% relevante
            </span>
          )}
        </div>

        <ThemeBadge value={post.theme} className="mt-3" />
        <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{post.content}</p>

        {post.source_url && (
          <a href={post.source_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-medium text-brand-600 hover:underline">
            Ver post original ↗
          </a>
        )}

        <div className="mt-4 flex gap-5 text-sm text-slate-400">
          <span className="inline-flex items-center gap-1.5"><HeartIcon className="h-4 w-4" />{post.likes || 0}</span>
          <span className="inline-flex items-center gap-1.5"><ChatBubbleLeftIcon className="h-4 w-4" />{post.comments_count || 0}</span>
          <span className="inline-flex items-center gap-1.5"><ArrowPathRoundedSquareIcon className="h-4 w-4" />{post.shares || 0}</span>
        </div>

        <hr className="my-6 border-slate-100" />

        <h3 className="mb-3 text-lg font-semibold text-slate-800">Comentários</h3>

        <form onSubmit={handleAddComment} className="mb-5">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Compartilhe suas ideias…"
            rows={3}
            required
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <button
            type="submit"
            disabled={sending}
            className="mt-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {sending ? 'Enviando…' : 'Comentar'}
          </button>
        </form>

        {(!post.comments || post.comments.length === 0) ? (
          <p className="text-sm text-slate-400">Nenhum comentário ainda.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {post.comments.map((comment) => (
              <div key={comment.id} className="rounded-lg border-l-2 border-brand-400 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">{comment.author?.full_name}</p>
                  <p className="text-xs text-slate-400">{formatDateTime(comment.created_at)}</p>
                </div>
                <p className="mt-1 text-sm text-slate-600">{comment.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Post;
